import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { saveWritingCorrectionLocally } from "@/utils/writingCorrectionCache";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  CheckCircle2, 
  AlertTriangle, 
  AlertCircle,
  Sparkles, 
  Clock, 
  BookOpen, 
  Bookmark, 
  Database,
  ArrowRight,
  Info,
  Loader2,
  TrendingUp,
  Target,
  HelpCircle,
  RefreshCw,
  Save
} from "lucide-react";
import { errorBankService } from "@/services/errorBankService";
import { historyService } from "@/services/historyService";
import { writingCorrectionHistoryService } from "@/services/writingCorrectionHistoryService";
import { flashcardService } from "@/services/flashcardService";
import { extractErrorSentenceContext, limitContextSentence } from "@/utils/textContextUtils";
import { WRITING_RUBRICS } from "@/data/writingRubric";

interface WritingCorrectionReportProps {
  correction: any; // Using any for robust backward compatibility with legacy history entries
  showToast: (message: string) => void;
  readOnly?: boolean;
  onRegenerate?: () => void;
  onAutoSave?: (record: any) => void;
  isAutoSaving?: boolean;
  autoSaved?: boolean;
  historyId?: string;
  originalWritingText?: string;
}

export default function WritingCorrectionReport({ 
  correction, 
  showToast, 
  readOnly = false,
  onRegenerate,
  onAutoSave,
  isAutoSaving = false,
  autoSaved = false,
  historyId,
  originalWritingText = "",
}: WritingCorrectionReportProps) {

  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [isSent, setIsSent] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const [isCreatingCards, setIsCreatingCards] = useState(false);
  const [isCardsCreated, setIsCardsCreated] = useState(false);
  const [createdIndividualCards, setCreatedIndividualCards] = useState<Record<string, boolean>>({});

  const [isSavingToHistory, setIsSavingToHistory] = useState(false);
  const [historySaveStatus, setHistorySaveStatus] = useState<"idle" | "success" | "error">("idle");

  async function handleSaveResultToHistory() {
    const realCorrection = correction;

    console.log("[EE_MANUAL_SAVE_CLICK]", JSON.stringify(realCorrection, null, 2));

    if (!realCorrection) {
      alert("Não foi possível localizar os dados da correção IA.");
      return;
    }

    try {
      setIsSavingToHistory(true);
      setHistorySaveStatus("idle");

      console.log("[EE_MANUAL_SAVE_WITH_TEXT]", {
        hasCorrection: !!realCorrection,
        originalTextLength: originalWritingText?.length,
        writingTasks: realCorrection.writingTasks || [],
      });

      const saved = await historyService.saveWritingCorrectionAsNewHistoryRecord({
        correctionResult: realCorrection,
        originalWritingText,
        writingTasks: realCorrection.writingTasks || [],
        section: "Completo",
        timeUsed: null,
        wordCount: null,
      });

      console.log("[EE_MANUAL_SAVE_DONE]", JSON.stringify(saved, null, 2));

      setHistorySaveStatus("success");
      alert("Resultado salvo no histórico com sucesso.");
    } catch (error) {
      console.error("[EE_MANUAL_SAVE_FAILED]", error);
      setHistorySaveStatus("error");
      alert("Não foi possível salvar o resultado no histórico. Veja o console.");
    } finally {
      setIsSavingToHistory(false);
    }
  }

  const hasCachedCorrectionRef = useRef(false);

  useEffect(() => {
    const realCorrection = correction;

    if (!realCorrection) return;
    if (hasCachedCorrectionRef.current) return;

    const resolvedHistoryId =
      historyId ||
      sessionStorage.getItem("currentWritingHistoryId") ||
      localStorage.getItem("currentWritingHistoryId");

    console.log("[EE_VISIBLE_CORRECTION_FOUND]", {
      historyId: resolvedHistoryId,
      realCorrection,
    });

    if (!resolvedHistoryId) {
      console.error("[EE_VISIBLE_CORRECTION_NO_HISTORY_ID]", {
        historyId,
        correction
      });
      return;
    }

    hasCachedCorrectionRef.current = true;

    saveWritingCorrectionLocally(resolvedHistoryId, {
      historyId: resolvedHistoryId,
      writingCorrection: realCorrection,
      writingTasks: realCorrection.writingTasks || [],
      sectionScores: realCorrection.sectionScores || null,
      score: realCorrection.totalScore || realCorrection.score || realCorrection.estimatedScore || null,
      maxScore: realCorrection.maxScore || 450,
      nclc: realCorrection.nclc || realCorrection.nclcLevel || null,
      cefr: realCorrection.cefr || realCorrection.cefrLevel || realCorrection.cecrl || null,
    });
  }, [correction, historyId]);

  // Expanded criteria documentation states
  const [expandedCrit, setExpandedCrit] = useState<Record<string, boolean>>({});
  
  // Regenerate Confirmation Modal
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);

  // handleSaveToHistory was removed as per user request for auto-save.
  // Instead, we now use onAutoSave passed from parent if needed, 
  // but TestResult should handle the primary persistence flow.

  const handleSendErrors = async () => {
    if (isSent) {
      showToast("Erros já foram enviados para o Banco de Erros.");
      return;
    }
    const errorsList = 
      correction.errors || 
      correction.identifiedErrors || 
      correction.errorAnalysis || 
      correction.correction?.sectionA?.errors || 
      correction.correction?.sectionB?.errors || 
      [];

    if (errorsList.length === 0) {
      showToast("Nenhum erro identificado para enviar.");
      return;
    }

    setIsSending(true);
    try {
      let addedCount = 0;
      let ignoredCount = 0;

      for (const err of errorsList) {
        const payload = {
          source: "writing_ai_correction",
          sourceCorrectionId: correction.id || "corr_id_temp",
          category: err.category || "Gramática",
          original: err.original,
          correction: err.corrected || err.original,
          corrected: err.corrected || err.original,
          explanation: err.explanation,
          severity: err.severity || "medium",
          context: limitContextSentence(extractErrorSentenceContext(correction.originalText || "", err.original)),
          competency: "writing",
          section: correction.section || "Section B"
        };

        try {
          await errorBankService.createError(payload);
          addedCount++;
        } catch (e: any) {
          if (e.message && e.message.includes("já está cadastrado")) {
            ignoredCount++;
          } else {
            console.error("Error sending error item to error bank:", e);
          }
        }
      }

      setIsSent(true);

      if (ignoredCount > 0 && addedCount > 0) {
        showToast(`${addedCount} novos erros enviados. ${ignoredCount} erro(s) já existia(m).`);
      } else if (ignoredCount > 0 && addedCount === 0) {
        showToast("Todos esses erros já estavam registrados no Banco de Erros.");
      } else {
        showToast(`${addedCount} erros enviados para o Banco de Erros com sucesso.`);
      }
    } catch (err) {
      console.error("Error saving errors:", err);
      showToast("Não foi possível enviar os erros para o Banco de Erros.");
    } finally {
      setIsSending(false);
    }
  };

  const handleCreateFlashcards = async () => {
    if (isCardsCreated) {
      showToast("Este flashcard já existe.");
      return;
    }
    const errorsList = 
      correction.errors || 
      correction.identifiedErrors || 
      correction.errorAnalysis || 
      correction.correction?.sectionA?.errors || 
      correction.correction?.sectionB?.errors || 
      [];
      
    if (errorsList.length === 0) {
      showToast("Nenhum erro disponível para criar flashcards.");
      return;
    }

    setIsCreatingCards(true);
    try {
      const result = await flashcardService.createFlashcardsFromWritingErrors({
        correctionId: correction.id || "corr_temp_id",
        errors: errorsList,
        section: correction.section,
        competency: "writing"
      });

      setIsCardsCreated(true);

      const updatedIndividuals: Record<string, boolean> = {};
      errorsList.forEach(e => {
        if (e.id) updatedIndividuals[e.id] = true;
      });
      setCreatedIndividualCards(updatedIndividuals);

      if (result.createdCount > 0) {
        showToast("Flashcard criado com sucesso.");
      } else {
        showToast("Este flashcard já existe.");
      }
    } catch (err: any) {
      console.error(err);
      if (err && err.message && err.message.includes("já existe")) {
        showToast("Este flashcard já existe.");
      } else {
        showToast("Não foi possível criar os flashcards.");
      }
    } finally {
      setIsCreatingCards(false);
    }
  };

  const handleCreateIndividualFlashcard = async (err: any) => {
    if (createdIndividualCards[err.id]) {
      showToast("Este flashcard já existe.");
      return;
    }

    try {
      const result = await flashcardService.createFlashcardsFromWritingErrors({
        correctionId: correction.id || "corr_temp_id",
        errors: [err],
        section: correction.section,
        competency: "writing"
      });

      setCreatedIndividualCards(prev => ({ ...prev, [err.id]: true }));

      if (result.createdCount > 0) {
        showToast("Flashcard criado com sucesso.");
      } else {
        showToast("Este flashcard já existe.");
      }
    } catch (error: any) {
      console.error(error);
      if (error && error.message && error.message.includes("já existe")) {
        showToast("Este flashcard já existe.");
      } else {
        showToast("Não foi possível criar o flashcard.");
      }
    }
  };

  const getSeverityBadge = (severity: "high" | "medium" | "low") => {
    switch (severity) {
      case "high":
        return <Badge className="bg-red-50 text-red-700 border border-red-100 hover:bg-red-100 text-[9px] font-bold">Grave</Badge>;
      case "medium":
        return <Badge className="bg-amber-50 text-amber-700 border border-amber-100 hover:bg-amber-100 text-[9px] font-bold">Médio</Badge>;
      case "low":
        return <Badge className="bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 text-[9px] font-bold">Leve</Badge>;
    }
  };

  const getSeverityBorder = (severity: "high" | "medium" | "low") => {
    switch (severity) {
      case "high":
        return "border-l-4 border-l-red-500 border-gray-100";
      case "medium":
        return "border-l-4 border-l-amber-500 border-gray-100";
      case "low":
        return "border-l-4 border-l-blue-500 border-gray-100";
    }
  };

  const getConfidenceBadge = (confidence: "high" | "medium" | "low") => {
    switch (confidence) {
      case "high":
        return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold rounded-lg py-0.5 px-2">Confiança Alta</Badge>;
      case "medium":
        return <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold rounded-lg py-0.5 px-2">Confiança Média</Badge>;
      case "low":
        return <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px] font-bold rounded-lg py-0.5 px-2">Confiança Baixa</Badge>;
      default:
        return <Badge className="bg-gray-50 text-gray-700 border border-gray-200 text-[10px] font-bold rounded-lg py-0.5 px-2">Confiança Indisponível</Badge>;
    }
  };

  const toggleCrit = (key: string) => {
    setExpandedCrit(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Safe Criteria Loader to support backwards compatibility of 4 vs 6 criteria seamlessly
  const allCriteriaKeys = ["taskAchievement", "coherence", "grammar", "vocabulary", "register", "thoughtDevelopment"];
  const availableCriteriaKeys = allCriteriaKeys.filter(key => correction.criteria && !!correction.criteria[key]);

  // Alerta de Tamanho de Palavras
  const isSectionA = correction.section === "Section A";
  const wordLimit = isSectionA ? 80 : 200;
  const showWordCountWarning = correction.wordCount < wordLimit;

  // Processamento e Agrupamento de Erros por Categoria
  const errorsList = correction.errors || [];
  const errorCountsByCategory: Record<string, number> = {};
  errorsList.forEach((err: any) => {
    const cat = err.category || "Outros";
    errorCountsByCategory[cat] = (errorCountsByCategory[cat] || 0) + 1;
  });

  const sortedCategories = Object.keys(errorCountsByCategory).sort((a, b) => errorCountsByCategory[b] - errorCountsByCategory[a]);

  const errorsGroupedByCategory: Record<string, any[]> = {};
  errorsList.forEach((err: any) => {
    const cat = err.category || "Outros";
    if (!errorsGroupedByCategory[cat]) {
      errorsGroupedByCategory[cat] = [];
    }
    errorsGroupedByCategory[cat].push(err);
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      
      {/* 1. WORD COUNT WARNING BANNER */}
      {showWordCountWarning && (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex items-start gap-3.5 text-amber-800 shadow-xs animate-in slide-in-from-top-2 duration-300">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-xs font-bold text-amber-900">Aviso Pedagógico: Extensão Insuficiente</p>
            <p className="text-[11px] text-amber-700 font-semibold leading-relaxed">
              Seu texto contém <span className="font-extrabold text-amber-950 font-mono">{correction.wordCount}</span> palavras. 
              Para a <span className="font-bold text-amber-950">{correction.section}</span>, recomenda-se produzir pelo menos 
              <span className="font-extrabold text-amber-950 font-mono"> {wordLimit}</span> palavras para abranger todos os aspectos necessários. 
              Textos muito curtos perdem pontos cruciais de <span className="underline">adequação à tarefa</span> e <span className="underline">desenvolvimento de ideias</span>.
            </p>
          </div>
        </div>
      )}

      {/* DYNAMIC TWO-COLUMN GRID */}
      <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-6 items-start">
        
        {/* COLUNA ESQUERDA: Métricas, Notas de Escala e Metas */}
        <div className="space-y-6">
          
          {/* CARD DE NOTA ESTIMADA E STATUS */}
          {/* CARD DE NOTA ESTIMADA E STATUS */}
          <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden">
            <div className="p-1.5 bg-gradient-to-r from-purple-600 to-indigo-600" />
            <CardContent className="p-5 space-y-5">
              
              {/* Informações básicas e badges */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[10px] font-bold border-purple-100 bg-purple-50/50 text-purple-700 rounded-lg">
                    {correction.section}
                  </Badge>
                  {(correction.source === "openai" || correction.source === "gemini") && (
                    <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[9px] font-extrabold rounded-lg px-2 py-0.5">
                      ✨ IA Real {correction.source === "gemini" ? "(Gemini)" : "(OpenAI)"}
                    </Badge>
                  )}
                  {correction.source === "mock_fallback" && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[9px] font-extrabold rounded-lg px-2 py-0.5">
                      ⚠️ Simulada
                    </Badge>
                  )}
                  {(correction.source === "frontend_fallback" || correction.source === "local_mock") && (
                    <Badge className="bg-red-100 text-red-800 border-red-200 text-[9px] font-extrabold rounded-lg px-2 py-0.5">
                      📴 Local
                    </Badge>
                  )}
                </div>
                <h3 className="text-sm font-bold text-slate-800">{correction.competencyLabel || "Expression Écrite (EE)"}</h3>
              </div>

              {/* Informações de palavras */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-100 rounded-xl font-bold text-xs text-gray-600">
                <Clock className="w-4 h-4 text-gray-400" />
                <span>{correction.wordCount} palavras analisadas</span>
              </div>

            </CardContent>
          </Card>

        </div>

        {/* COLUNA DIREITA: Feedbacks, Critérios e Avaliação */}
        <div className="space-y-6">
          
          {/* FEEDBACK GERAL DA IA */}
          <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden">
            <CardHeader className="pb-3 border-b border-gray-50 bg-slate-50/25">
              <CardTitle className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Sparkles className="w-4.5 h-4.5 text-purple-600" />
                Feedback Geral da IA
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              <p className="text-slate-700 text-sm font-medium leading-relaxed font-serif whitespace-pre-wrap">
                "{correction.overallComment}"
              </p>
              
              <div className="bg-amber-50/50 border border-amber-100/60 rounded-xl p-3 flex items-start gap-2.5">
                <AlertCircle className="w-4.5 h-4.5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <span className="text-[10px] font-bold text-amber-800">Aviso Importante</span>
                  <p className="text-[10px] text-amber-700 font-semibold leading-relaxed">
                    Esta correção é uma estimativa pedagógica gerada por IA baseada na régua oficial do TEF Canada. Ela não substitui uma correção oficial.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* NOTAS POR CRITÉRIOS */}
          <Card className="border-none shadow-sm bg-white rounded-2xl">
            <CardHeader className="pb-3 border-b border-gray-50">
              <CardTitle className="text-sm font-bold text-slate-800">Notas por Critérios Analíticos</CardTitle>
              <CardDescription className="text-xs">Pontuações pedagógicas de acordo com a régua oficial do TEF.</CardDescription>
            </CardHeader>
            <CardContent className="py-5 space-y-4">
              {availableCriteriaKeys.map((key) => {
                const crit = correction.criteria[key];
                const pct = (crit.score / crit.maxScore) * 100;
                const rubricInfo = WRITING_RUBRICS[key];

                return (
                  <div key={key} className="space-y-2 p-3 bg-slate-50/25 rounded-xl border border-slate-100/40">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-gray-700">{crit.label}</span>
                        {rubricInfo && (
                          <button 
                            onClick={() => toggleCrit(key)}
                            className="text-gray-400 hover:text-purple-600 transition-colors focus:outline-none"
                            title="Ver detalhes da rubrica oficial"
                          >
                            <HelpCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <span className="font-black text-gray-900 font-mono text-[13px] bg-white px-2 py-0.5 rounded-lg border border-slate-100/50 shadow-xs">
                        {crit.score} <span className="text-[10px] text-gray-400 font-bold">/ {crit.maxScore}</span>
                      </span>
                    </div>
                    <Progress value={pct} className="h-1.5" />
                    <p className="text-[11px] text-gray-600 leading-normal font-semibold pl-0.5">
                      {crit.comment}
                    </p>

                    {expandedCrit[key] && rubricInfo && (
                      <div className="mt-2.5 p-3.5 bg-purple-50/30 border border-purple-100/40 rounded-xl text-[10px] space-y-1.5 animate-in slide-in-from-top-1 duration-150 text-purple-950 font-semibold leading-relaxed">
                        <div>
                          <span className="font-black uppercase text-purple-900 text-[9px] block mb-0.5">O que é avaliado:</span>
                          <p className="text-purple-900/80">{rubricInfo.description}</p>
                        </div>
                        <div className="pt-1.5 border-t border-purple-100/40">
                          <span className="font-black uppercase text-purple-900 text-[9px] block mb-0.5">Diretriz pedagógica:</span>
                          <p className="text-purple-800">{rubricInfo.guidance}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* EXIGÊNCIAS DA SEÇÃO */}
          {correction.sectionSpecificFeedback && (
            <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden">
              <CardHeader className="pb-3 border-b border-gray-50 bg-slate-50/20">
                <CardTitle className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                  Exigências da {correction.section}
                </CardTitle>
                <CardDescription className="text-xs">Critérios e expectativas pedagógicas específicas do enunciado.</CardDescription>
              </CardHeader>
              <CardContent className="py-5 space-y-4">
                <div className="space-y-1">
                  <span className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider block">O que é avaliado nesta seção:</span>
                  <p className="text-xs text-gray-600 font-semibold leading-relaxed">
                    {correction.sectionSpecificFeedback.expectedTask}
                  </p>
                </div>
                <div className="pt-3 border-t border-slate-50 space-y-1">
                  <span className="text-[10px] text-purple-600 font-extrabold uppercase tracking-wider block">Análise direcionada:</span>
                  <p className="text-xs text-gray-700 font-semibold leading-relaxed font-serif">
                    "{correction.sectionSpecificFeedback.taskSpecificComment}"
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* PONTOS FORTES E FRACOS */}
          <Card className="border-none shadow-sm bg-white rounded-2xl">
            <CardHeader className="pb-3 border-b border-gray-50">
              <CardTitle className="text-sm font-bold text-slate-800">Pontos Fortes e Fracos</CardTitle>
              <CardDescription className="text-xs">Principais destaques identificados na sua escrita.</CardDescription>
            </CardHeader>
            <CardContent className="py-5 space-y-6">
              {/* Strengths */}
              <div className="space-y-2.5">
                <span className="text-[10px] text-emerald-600 font-extrabold uppercase tracking-wider block">Pontos Fortes</span>
                <ul className="space-y-1.5">
                  {correction.strengths.map((st: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-xs font-semibold text-gray-600 leading-normal">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{st}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Weaknesses */}
              <div className="space-y-2.5 pt-4 border-t border-slate-50">
                <span className="text-[10px] text-amber-600 font-extrabold uppercase tracking-wider block">Pontos a Melhorar</span>
                <ul className="space-y-1.5">
                  {correction.weaknesses.map((wk: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-xs font-semibold text-gray-600 leading-normal">
                      <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <span>{wk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

        </div>

      </div>

      {/* 6. ERRORS FOUND GROUPED BY CATEGORY WITH SUMMARIES */}
      <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden">
        <CardHeader className="pb-0 border-b border-gray-50 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 py-4">
          <div>
            <CardTitle className="text-sm font-bold text-slate-800">Erros Identificados ({errorsList.length})</CardTitle>
            <CardDescription className="text-xs">Aprenda com as correções gramaticais e ajustes estruturais sugeridos.</CardDescription>
          </div>
        </CardHeader>

        {/* Categories badged summaries */}
        {errorsList.length > 0 && (
          <div className="p-4 bg-slate-50 border-b border-gray-50 flex flex-wrap gap-2.5 items-center">
            <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider mr-1">Resumo dos Erros:</span>
            {sortedCategories.map(cat => (
              <Badge key={cat} variant="outline" className="text-[10px] font-bold bg-white text-gray-700 border-gray-200 py-1 px-2.5 rounded-lg flex items-center gap-1.5 shadow-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                <span>{cat}:</span>
                <span className="font-mono text-red-600 font-black">{errorCountsByCategory[cat]}</span>
              </Badge>
            ))}
          </div>
        )}

        <CardContent className="p-0">
          {errorsList.length === 0 ? (
            <div className="p-10 text-center flex flex-col items-center justify-center space-y-2">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              <p className="text-xs font-bold text-slate-700">Nenhum erro linguístico identificado!</p>
              <p className="text-[10px] text-gray-400 leading-relaxed max-w-xs">Sua redação está com controle gramatical excelente.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sortedCategories.map(cat => (
                <div key={cat} className="p-5 space-y-4">
                  {/* Category Section Header */}
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-3.5 bg-red-500 rounded-xs" />
                    <h4 className="text-xs font-black text-gray-800 uppercase tracking-wider">
                      {cat} <span className="text-gray-400 font-bold">({errorCountsByCategory[cat]} {errorCountsByCategory[cat] === 1 ? 'ocorrência' : 'ocorrências'})</span>
                    </h4>
                  </div>

                  {/* Errors items in category */}
                  <div className="space-y-4 pt-1">
                    {errorsGroupedByCategory[cat].map((err: any) => (
                      <div 
                        key={err.id} 
                        className={`p-4 bg-white hover:bg-slate-50/20 rounded-2xl border border-gray-100 transition-all flex flex-col md:flex-row gap-4 items-start ${getSeverityBorder(err.severity)} shadow-xs`}
                      >
                        <div className="space-y-1.5 flex-1 w-full">
                          <div className="flex items-center gap-2 flex-wrap">
                            {getSeverityBadge(err.severity)}
                          </div>
                          
                          <div className="space-y-1 pt-1">
                            <div className="flex items-start gap-2 text-xs font-medium text-red-700 bg-red-50/40 border border-red-100/50 p-2.5 rounded-xl font-mono w-full">
                              <span className="font-extrabold text-[10px] uppercase text-red-500 shrink-0 mt-0.5">Original:</span>
                              <span className="line-through leading-relaxed whitespace-pre-wrap">{err.original}</span>
                            </div>
                            
                            <div className="flex items-start gap-2 text-xs font-semibold text-emerald-800 bg-emerald-50/40 border border-emerald-100/50 p-2.5 rounded-xl font-mono w-full">
                              <span className="font-extrabold text-[10px] uppercase text-emerald-600 shrink-0 mt-0.5">Sugerido:</span>
                              <span className="leading-relaxed whitespace-pre-wrap">{err.corrected}</span>
                            </div>
                          </div>
                        </div>

                        <div className="md:w-1/3 bg-gray-50/50 rounded-2xl p-4 border border-gray-100/70 h-full flex flex-col justify-between items-start gap-3 w-full md:max-w-[280px]">
                          <div className="w-full">
                            <span className="text-[9px] font-black uppercase text-gray-400 block tracking-wider mb-1">Explicação Didática</span>
                            <p className="text-[10.5px] text-gray-600 leading-relaxed font-semibold">
                              {err.explanation}
                            </p>
                          </div>
                          <div className="pt-2 border-t border-gray-100 w-full flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={createdIndividualCards[err.id]}
                              onClick={() => handleCreateIndividualFlashcard(err)}
                              className={`text-[10px] font-bold rounded-lg h-7 px-2.5 flex items-center gap-1 transition-all duration-150 ${
                                createdIndividualCards[err.id]
                                  ? "bg-purple-50 text-purple-700 hover:bg-purple-50 hover:text-purple-700"
                                  : "text-purple-600 hover:text-purple-700 hover:bg-purple-50/80"
                              }`}
                            >
                              <BookOpen className="w-3 h-3" />
                              {createdIndividualCards[err.id] ? "Flashcard criado" : "Criar flashcard"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* IMPROVED VERSION */}
      <Card className="border-none shadow-sm bg-white rounded-2xl overflow-hidden">
        <div className="p-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
        <CardHeader className="pb-3 border-b border-gray-50 bg-slate-50/30">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold text-slate-800">Redação Otimizada pela IA</CardTitle>
            <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-none text-[10px] font-bold">Nível C1 Recomendado</Badge>
          </div>
          <CardDescription className="text-xs">Versão ajustada para obter pontuações máximas, preservando seus argumentos e ideias originais.</CardDescription>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 font-serif leading-relaxed text-gray-800 text-sm whitespace-pre-wrap select-all">
            {correction.improvedVersion}
          </div>
        </CardContent>
      </Card>

      {/* STUDY RECOMMENDATIONS */}
      <Card className="border-none shadow-sm bg-white rounded-2xl">
        <CardHeader className="pb-3 border-b border-gray-50">
          <CardTitle className="text-sm font-bold text-slate-800">Sugestões de Estudos Personalizadas</CardTitle>
          <CardDescription className="text-xs">Foque nos temas abaixo para acelerar sua evolução de proficiência.</CardDescription>
        </CardHeader>
        <CardContent className="py-5">
          <div className="grid gap-3 sm:grid-cols-2">
            {correction.studySuggestions.map((sug: string, idx: number) => (
              <div 
                key={idx} 
                className="p-3.5 bg-indigo-50/20 hover:bg-indigo-50/40 border border-indigo-100/30 rounded-xl flex items-center gap-3 transition-all duration-150"
              >
                <div className="w-7 h-7 bg-indigo-50 rounded-lg text-indigo-600 flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
                  {idx + 1}
                </div>
                <span className="text-xs font-semibold text-gray-700 leading-snug">{sug}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ACTIONS ROW WITH REGENERATE */}
      <div className="bg-slate-50/70 border border-slate-100/70 p-5 rounded-2xl flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center justify-end">
        
        {(autoSaved || isAutoSaving) && (
          <div className={`px-4 h-10 flex items-center gap-2 rounded-xl border text-xs font-bold transition-all duration-300 ${
            autoSaved 
              ? "bg-emerald-50 border-emerald-100 text-emerald-700" 
              : "bg-purple-50 border-purple-100 text-purple-700 animate-pulse"
          }`}>
            {isAutoSaving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Salvando automaticamente...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-3.5 h-3.5" />
                Salvo automaticamente no histórico
              </>
            )}
          </div>
        )}

        {/* 7. REGENERATE CORRECTION TRIGGER ACTION BUTTON */}
        {!readOnly && (
          <Button 
            variant="outline" 
            onClick={handleSaveResultToHistory}
            disabled={isSavingToHistory || historySaveStatus === "success"}
            className={`rounded-xl border-emerald-200 font-bold text-xs h-10 flex items-center justify-center gap-1.5 transition-all duration-200 shrink-0 ${
              historySaveStatus === "success" 
                ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                : "bg-white text-emerald-600 hover:bg-emerald-50/50"
            }`}
          >
            {isSavingToHistory ? (
              <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
            ) : historySaveStatus === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            ) : (
              <Save className="w-4 h-4 text-emerald-400" />
            )}
            {isSavingToHistory ? "Salvando..." : historySaveStatus === "success" ? "Resultado salvo" : "Salvar no Histórico"}
          </Button>
        )}

        {!readOnly && onRegenerate && (
          <Button 
            variant="outline" 
            onClick={() => setShowRegenerateModal(true)}
            className="rounded-xl border-purple-200 bg-white text-purple-700 hover:bg-purple-50 font-bold text-xs h-10 flex items-center justify-center gap-1.5 transition-all duration-200 shadow-xs shrink-0"
          >
            <RefreshCw className="w-3.5 h-3.5 text-purple-600" />
            Regenerar correção
          </Button>
        )}

        <Button 
          variant="outline" 
          onClick={handleSendErrors}
          disabled={isSending || isSent}
          className={`rounded-xl border-gray-200 font-bold text-xs h-10 flex items-center justify-center gap-1.5 transition-all duration-200 shrink-0 ${
            isSent 
              ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
              : "bg-white text-gray-600 hover:bg-gray-50/80"
          }`}
        >
          {isSending ? (
            <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
          ) : isSent ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          ) : (
            <Database className="w-4 h-4 text-gray-400" />
          )}
          {isSending ? "Enviando..." : isSent ? "Erros enviados" : "Enviar para Banco de Erros"}
        </Button>

        <Button 
          variant="outline" 
          onClick={handleCreateFlashcards}
          disabled={isCreatingCards || isCardsCreated}
          className={`rounded-xl border-gray-200 font-bold text-xs h-10 flex items-center justify-center gap-1.5 transition-all duration-200 shrink-0 ${
            isCardsCreated 
              ? "bg-purple-50 text-purple-700 border-purple-200" 
              : "bg-white text-gray-600 hover:bg-gray-50/80"
          }`}
        >
          {isCreatingCards ? (
            <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
          ) : isCardsCreated ? (
            <CheckCircle2 className="w-4 h-4 text-purple-600" />
          ) : (
            <BookOpen className="w-4 h-4 text-gray-400" />
          )}
          {isCreatingCards ? "Criando..." : isCardsCreated ? "Flashcards criados" : "Criar flashcards dos erros"}
        </Button>
      </div>

      {/* 8. MODAL DE CONFIRMAÇÃO DE REGENERAÇÃO */}
      {showRegenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <Card className="w-full max-w-md border-none shadow-2xl bg-white rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-1.5 bg-gradient-to-r from-purple-600 to-indigo-600" />
            <CardHeader className="pt-6 pb-4">
              <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center mb-3">
                <RefreshCw className="w-6 h-6 text-purple-600 animate-spin-slow" />
              </div>
              <CardTitle className="text-base font-black text-gray-900">Deseja gerar uma nova correção?</CardTitle>
              <CardDescription className="text-xs text-gray-500 leading-normal font-semibold">
                Isso consumirá uma nova correção do seu limite diário. O relatório atual da tela será atualizado com os novos dados de avaliação gerados pela IA.
              </CardDescription>
            </CardHeader>
            <CardFooter className="bg-gray-50/50 p-4 flex gap-3 justify-end border-t border-gray-100">
              <Button 
                variant="outline" 
                onClick={() => setShowRegenerateModal(false)}
                className="rounded-xl border-gray-200 font-bold text-xs"
              >
                Cancelar
              </Button>
              <Button 
                onClick={() => {
                  setShowRegenerateModal(false);
                  if (onRegenerate) onRegenerate();
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-xs px-4"
              >
                Confirmar e Corrigir
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
      
    </div>
  );
}
